import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { VimeoAPI, VimeoVideo, initializeVimeoOAuth, exchangeVimeoCode } from '@/lib/vimeo';
import { YouTubeAPI, YouTubeVideo } from '@/lib/youtube';
import { VideoData, CategoryData, saveVideo, stringToTimestamp } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { Check, X, Plus, Save, Youtube, Video } from 'lucide-react';
import AddCategoryModal from './add-category-modal';
import { logger } from '@/lib/logger';

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  categories: CategoryData[];
  onVideosSaved: () => void;
  onCategoriesUpdated: () => void;
}

// Combined video interface for both platforms
interface CombinedVideo {
  id: string;
  title: string;
  description: string;
  duration: number; // in seconds
  thumbnailUrl: string;
  platform: 'vimeo' | 'youtube';
  originalData: VimeoVideo | YouTubeVideo;
}

export default function ImportModal({ open, onClose, categories, onVideosSaved, onCategoriesUpdated }: ImportModalProps) {
  const [step, setStep] = useState(1);
  const [vimeoConnected, setVimeoConnected] = useState(false);
  const [vimeoAPI, setVimeoAPI] = useState<VimeoAPI | null>(null);
  const [youtubeAPI, setYoutubeAPI] = useState<YouTubeAPI | null>(null);
  const [youtubeChannelId, setYoutubeChannelId] = useState('UCsq-0S4Ubfo0PnhVsfP-saA');
  const [youtubePlaylistId, setYoutubePlaylistId] = useState('PLFgquLnL59alCl_2TQvOiD5Vgm1hCaGSI');
  const [enableVimeoImport, setEnableVimeoImport] = useState(true);
  const [enableYouTubeImport, setEnableYouTubeImport] = useState(true);
  const [combinedVideos, setCombinedVideos] = useState<CombinedVideo[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [videoAssignments, setVideoAssignments] = useState<Record<string, { categoryId: string; publishDate: string; contentType: string }>>({});
  const [loading, setLoading] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [importProgress, setImportProgress] = useState<{ 
    vimeo: { status: string; count?: number }; 
    youtube: { status: string; count?: number }; 
  }>({ 
    vimeo: { status: '' }, 
    youtube: { status: '' } 
  });
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      // Initialize YouTube API - we'll use server-side proxy
      setYoutubeAPI(new YouTubeAPI('server-proxy'));
      
      // Check if we're returning from Vimeo OAuth
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        handleVimeoCallback(code);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [open, user]);

  const handleVimeoCallback = async (code: string) => {
    try {
      setLoading(true);
      setImportProgress(prev => ({ 
        ...prev, 
        vimeo: { status: 'Connecting...' } 
      }));
      const accessToken = await exchangeVimeoCode(code);
      const api = new VimeoAPI(accessToken);
      
      // Test the connection
      await api.getCurrentUser();
      
      setVimeoAPI(api);
      setVimeoConnected(true);
      
      // Load videos from both platforms
      await loadAllVideos(api, youtubeAPI);
      setStep(2);
      
      toast({
        title: "Import completed",
        description: "Videos from connected platforms are now available for import.",
      });
    } catch (error: any) {
      toast({
        title: "Failed to connect to Vimeo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setImportProgress({ vimeo: { status: '' }, youtube: { status: '' } });
    }
  };

  const loadAllVideos = async (vimeoApi?: VimeoAPI | null, youtubeApi?: YouTubeAPI | null) => {
    const allVideos: CombinedVideo[] = [];
    
    logger.log('Import flags:', { enableVimeoImport, enableYouTubeImport });
    
    try {
      // Load Vimeo videos (only if enabled)
      if (vimeoApi && enableVimeoImport) {
        setImportProgress(prev => ({ 
          ...prev, 
          vimeo: { status: 'Loading videos...' } 
        }));
        const vimeoResponse = await vimeoApi.getUserVideos(1, 50);
        logger.log('VimeoAPI response:', vimeoResponse);
        
        for (const vimeoVideo of vimeoResponse.data) {
          const videoId = VimeoAPI.getVideoIdFromUri(vimeoVideo.uri);
          const thumbnailUrl = VimeoAPI.getBestThumbnail(vimeoVideo) || '';
          
          allVideos.push({
            id: `vimeo_${videoId}`,
            title: vimeoVideo.name,
            description: vimeoVideo.description || '',
            duration: vimeoVideo.duration,
            thumbnailUrl,
            platform: 'vimeo',
            originalData: vimeoVideo,
          });
        }
        setImportProgress(prev => ({ 
          ...prev, 
          vimeo: { status: `Found ${vimeoResponse.data.length} videos`, count: vimeoResponse.data.length } 
        }));
      }

      // Load YouTube videos from channel or playlist (only if enabled)
      logger.log('Checking YouTube import conditions:', { 
        youtubeApi: !!youtubeApi, 
        youtubeChannelId: youtubeChannelId.trim(), 
        youtubePlaylistId: youtubePlaylistId.trim(),
        enableYouTubeImport
      });
      
      // Handle disabled Vimeo import
      if (vimeoApi && !enableVimeoImport) {
        setImportProgress(prev => ({ 
          ...prev, 
          vimeo: { status: 'Import disabled in settings', count: 0 } 
        }));
      }

      if (youtubeApi && enableYouTubeImport && (youtubeChannelId.trim() || youtubePlaylistId.trim())) {
        try {
          setImportProgress(prev => ({ 
            ...prev, 
            youtube: { status: youtubePlaylistId ? 'Loading playlist...' : 'Finding channel...' } 
          }));
          
          let youtubeVideos: any[] = [];
          
          // If playlist ID is provided, use playlist import (preferred for unlisted content)
          if (youtubePlaylistId.trim()) {
            logger.log(`Loading YouTube playlist: ${youtubePlaylistId}`);
            try {
              setImportProgress(prev => ({ 
                ...prev, 
                youtube: { status: 'Fetching playlist videos...' } 
              }));
              youtubeVideos = await youtubeApi.getPlaylistVideos(youtubePlaylistId, 50);
              logger.log(`Found ${youtubeVideos.length} videos in playlist (including unlisted)`);
            } catch (error: any) {
              logger.error('Playlist loading failed:', error);
              setImportProgress(prev => ({ 
                ...prev, 
                youtube: { status: `Playlist error: ${error.message}` } 
              }));
              toast({
                title: "YouTube playlist error",
                description: `Failed to load playlist: ${error.message}`,
                variant: "destructive",
              });
            }
          }
          
          // If no playlist or playlist failed, try channel import
          if (youtubeVideos.length === 0 && youtubeChannelId.trim()) {
            logger.log(`Looking for YouTube channel: ${youtubeChannelId}`);
            let channel = null;
            
            // Try different methods to find the channel
            if (youtubeChannelId.startsWith('@')) {
              channel = await youtubeApi.getChannelByHandle(youtubeChannelId);
            } else if (youtubeChannelId.startsWith('UC') && youtubeChannelId.length === 24) {
              channel = await youtubeApi.getChannelById(youtubeChannelId);
            } else {
              channel = await youtubeApi.getChannelByUsername(youtubeChannelId);
            }

            if (channel) {
              setImportProgress(prev => ({ 
                ...prev, 
                youtube: { status: 'Loading videos...' } 
              }));
              logger.log(`Found YouTube channel: ${channel.title} (${channel.id})`);
              
              // Debug channel access for troubleshooting
              if (channel.id === 'UCsq-0S4Ubfo0PnhVsfP-saA') {
                await youtubeApi.debugChannelAccess(channel.id);
              }
              
              setImportProgress(prev => ({ 
                ...prev, 
                youtube: { status: 'Searching for videos and Shorts...' } 
              }));
              youtubeVideos = await youtubeApi.getChannelVideos(channel.id, 50);
              logger.log(`Found ${youtubeVideos.length} YouTube videos (including Shorts)`);
            }
          }
          
          // Process YouTube videos and add to combined list
          for (const youtubeVideo of youtubeVideos) {
            const duration = YouTubeAPI.parseDuration(youtubeVideo.duration);
            const thumbnailUrl = YouTubeAPI.getBestThumbnail(youtubeVideo) || '';
            
            allVideos.push({
              id: `youtube_${youtubeVideo.id}`,
              title: youtubeVideo.title,
              description: youtubeVideo.description,
              duration,
              thumbnailUrl,
              platform: 'youtube',
              originalData: youtubeVideo,
            });
          }
          
          if (youtubeVideos.length === 0) {
            const source = youtubePlaylistId.trim() ? 'playlist' : 'channel';
            setImportProgress(prev => ({ 
              ...prev, 
              youtube: { status: `No videos found in ${source}`, count: 0 } 
            }));
            toast({
              title: "No YouTube videos found",
              description: `No videos accessible via API from the configured ${source}. For unlisted content, ensure videos are in a public playlist.`,
              variant: "destructive",
            });
          } else {
            setImportProgress(prev => ({ 
              ...prev, 
              youtube: { status: `Found ${youtubeVideos.length} videos`, count: youtubeVideos.length } 
            }));
          }
        } catch (error: any) {
          logger.error('YouTube API error:', error);
          setImportProgress(prev => ({ 
            ...prev, 
            youtube: { status: `Error: ${error.message}` } 
          }));
          toast({
            title: "YouTube API error",
            description: error.message,
            variant: "destructive",
          });
        }
      }

      // Handle disabled YouTube import
      if (youtubeApi && !enableYouTubeImport) {
        setImportProgress(prev => ({ 
          ...prev, 
          youtube: { status: 'Import disabled in settings', count: 0 } 
        }));
      }

      // If both imports are disabled, show appropriate message
      if (!enableVimeoImport && !enableYouTubeImport) {
        toast({
          title: "Import skipped",
          description: "Both video imports are disabled in settings. Use the Import page to process existing draft videos.",
        });
        
        setImportProgress({
          vimeo: { status: 'Import disabled', count: 0 },
          youtube: { status: 'Import disabled', count: 0 }
        });
      }

      setCombinedVideos(allVideos);
      logger.log(`Loaded ${allVideos.length} videos from ${vimeoApi ? 'Vimeo' : ''}${vimeoApi && youtubeApi && (youtubeChannelId || youtubePlaylistId) ? ' and ' : ''}${youtubeApi && (youtubeChannelId || youtubePlaylistId) ? 'YouTube' : ''}`);
      


    } catch (error: any) {
      toast({
        title: "Failed to load videos",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const connectVimeo = () => {
    initializeVimeoOAuth();
  };

  const startImport = async () => {
    // If both imports are disabled, redirect to import page for draft processing
    if (!enableVimeoImport && !enableYouTubeImport) {
      toast({
        title: "Processing existing videos",
        description: "Both imports disabled. Redirecting to process existing draft videos.",
      });
      setTimeout(() => {
        handleClose();
        window.location.href = '/import';
      }, 1500);
      return;
    }

    // Check if any enabled platforms are configured
    const vimeoEnabled = enableVimeoImport && vimeoConnected;
    const youtubeEnabled = enableYouTubeImport && (youtubeChannelId.trim() || youtubePlaylistId.trim());
    
    if (!vimeoEnabled && !youtubeEnabled) {
      const missingVimeo = enableVimeoImport && !vimeoConnected;
      const missingYoutube = enableYouTubeImport && !youtubeChannelId.trim() && !youtubePlaylistId.trim();
      
      let description = "Please ";
      if (missingVimeo && missingYoutube) {
        description += "connect Vimeo and configure YouTube in Settings.";
      } else if (missingVimeo) {
        description += "connect Vimeo.";
      } else if (missingYoutube) {
        description += "configure YouTube channel/playlist in Settings.";
      }
      
      toast({
        title: "No platforms ready",
        description,
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      setStep(2);
      await loadAllVideos(vimeoAPI, youtubeAPI);
      
      toast({
        title: "Videos loaded",
        description: "Ready to select videos for import.",
      });
    } catch (error: any) {
      toast({
        title: "Failed to load videos",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleVideoSelection = (videoId: string) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId);
    } else {
      newSelected.add(videoId);
    }
    setSelectedVideos(newSelected);
  };

  const selectAllVideos = () => {
    if (selectedVideos.size === combinedVideos.length) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(combinedVideos.map(v => v.id)));
    }
  };

  const proceedToAssignment = () => {
    setStep(3);
    
    // Initialize assignments for selected videos
    const assignments: Record<string, { categoryId: string; publishDate: string; contentType: string }> = {};
    selectedVideos.forEach(videoId => {
      assignments[videoId] = {
        categoryId: '',
        publishDate: new Date().toISOString().slice(0, 16), // Current date/time in datetime-local format
        contentType: 'information', // Default to information (lowercase)
      };
    });
    setVideoAssignments(assignments);
  };

  const updateVideoAssignment = (videoId: string, field: 'categoryId' | 'publishDate' | 'contentType', value: string) => {
    setVideoAssignments(prev => ({
      ...prev,
      [videoId]: {
        ...prev[videoId],
        [field]: value,
      },
    }));
  };

  const saveToFirestore = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const selectedVideoData = combinedVideos.filter(video => 
        selectedVideos.has(video.id)
      );

      for (const combinedVideo of selectedVideoData) {
        const assignment = videoAssignments[combinedVideo.id];
        
        let videoData: VideoData;
        
        if (combinedVideo.platform === 'vimeo') {
          const vimeoVideo = combinedVideo.originalData as VimeoVideo;
          const videoId = VimeoAPI.getVideoIdFromUri(vimeoVideo.uri);
          
          // Extract tags from Vimeo video
          const externalTags = vimeoVideo.tags ? vimeoVideo.tags.map((tagObj: any) => tagObj.tag) : [];
          logger.log(`Video "${vimeoVideo.name}" has ${externalTags.length} tags:`, externalTags);
          
          const directFileUrl = VimeoAPI.getBestDirectFileUrl(vimeoVideo);
          const thumbnailUrl = VimeoAPI.getBestThumbnail(vimeoVideo);
          
          logger.log(`Processing Vimeo video "${vimeoVideo.name}":`, {
            hasFiles: !!vimeoVideo.files,
            filesCount: vimeoVideo.files?.length || 0,
            directFileUrl: directFileUrl || 'not available',
            thumbnailUrl: thumbnailUrl || 'not available'
          });

          videoData = {
            externalId: videoId,
            title: vimeoVideo.name,
            description: vimeoVideo.description || '',
            author: vimeoVideo.name.split(' - ')[0] || 'Unknown',
            channel: 'Vimeo',
            duration: `0:${Math.floor(vimeoVideo.duration / 60)}:${String(vimeoVideo.duration % 60).padStart(2, '0')}.000000`,
            mediaType: 'vimeo' as const,
            contentCategories: assignment.categoryId ? [assignment.categoryId] : [],
            contentType: assignment.contentType,
            jobTypes: [],
            enabled: true,
            status: 'Draft' as const,
            url: vimeoVideo.player_embed_url || '',
            content: vimeoVideo.description || '',
            publishDate: stringToTimestamp(assignment.publishDate),
            externalTags: externalTags,
            directFileUrl: directFileUrl || undefined,
            thumbnailUrl: thumbnailUrl || undefined,
          };
        } else {
          // YouTube video
          const youtubeVideo = combinedVideo.originalData as YouTubeVideo;
          
          logger.log(`Processing YouTube video "${youtubeVideo.title}"`);

          videoData = {
            externalId: youtubeVideo.id,
            title: youtubeVideo.title,
            description: youtubeVideo.description || '',
            author: youtubeVideo.channelTitle || 'Unknown',
            channel: youtubeVideo.channelTitle || 'YouTube',
            duration: `0:${Math.floor(combinedVideo.duration / 60)}:${String(combinedVideo.duration % 60).padStart(2, '0')}.000000`,
            mediaType: 'youtube' as const,
            contentCategories: assignment.categoryId ? [assignment.categoryId] : [],
            contentType: assignment.contentType,
            jobTypes: [],
            enabled: true,
            status: 'Draft' as const,
            url: YouTubeAPI.getEmbedUrl(youtubeVideo.id),
            content: youtubeVideo.description || '',
            publishDate: stringToTimestamp(assignment.publishDate),
            externalTags: [],
            thumbnailUrl: YouTubeAPI.getBestThumbnail(youtubeVideo) || undefined,
          };
        }
        
        await saveVideo(videoData);
      }
      
      logger.log(`Successfully imported ${selectedVideoData.length} videos to Firebase`);
      
    } catch (error: any) {
      logger.error('Error importing videos to Firebase:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedVideos(new Set());
    setVideoAssignments({});
    setCombinedVideos([]);
    setImportProgress({ vimeo: { status: '' }, youtube: { status: '' } });
    onClose();
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Videos from Vimeo & YouTube</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Step 1: Platform Connection */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
                <h3 className="text-lg font-medium text-slate-900">Connect Video Platforms</h3>
              </div>
              
              {/* Vimeo Connection */}
              {!vimeoConnected ? (
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        <Video size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Connect Vimeo Account</p>
                        <p className="text-sm text-slate-600">Authorize access to import your videos</p>
                      </div>
                    </div>
                    <Button onClick={connectVimeo} className="bg-blue-600 hover:bg-blue-700">
                      Connect
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="text-green-600" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Vimeo Connected</p>
                      <p className="text-sm text-slate-600">Ready to import videos</p>
                    </div>
                  </div>
                </div>
              )}

              {/* YouTube Channel Input */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-semibold">
                    <Youtube size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">YouTube Channel</p>
                    <p className="text-sm text-slate-600">
                      {youtubeChannelId || youtubePlaylistId ? 
                        `Configured: ${youtubeChannelId || 'Channel'} ${youtubePlaylistId ? '+ Playlist' : ''}` : 
                        'Configure in Settings to enable YouTube import'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 mt-2">
                  <Input
                    placeholder="Channel: @channelhandle, username, or UC1234567890123456789012"
                    value={youtubeChannelId}
                    onChange={(e) => setYoutubeChannelId(e.target.value)}
                    disabled
                  />
                  <Input
                    placeholder="Playlist: PLxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={youtubePlaylistId}
                    onChange={(e) => setYoutubePlaylistId(e.target.value)}
                    disabled
                  />
                  <p className="text-xs text-slate-500">Configure these in Settings. Playlist import accesses unlisted videos.</p>
                </div>
              </div>

              {/* Import Progress */}
              {(importProgress.vimeo.status || importProgress.youtube.status) && (
                <div className="space-y-3">
                  {importProgress.vimeo.status && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        {importProgress.vimeo.status.includes('Loading') ? (
                          <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                        ) : (
                          <Video className="h-4 w-4 text-green-600" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">Vimeo</p>
                          <p className="text-sm text-slate-600">{importProgress.vimeo.status}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {importProgress.youtube.status && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        {importProgress.youtube.status.includes('Loading') || importProgress.youtube.status.includes('Finding') ? (
                          <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full"></div>
                        ) : (
                          <Youtube className="h-4 w-4 text-red-600" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">YouTube</p>
                          <p className="text-sm text-slate-600">{importProgress.youtube.status}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Start Import Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={startImport} 
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90"
                >
                  {loading ? 'Loading...' : 
                    (!enableVimeoImport && !enableYouTubeImport) ? 'Process Draft Videos' :
                    'Start Import'
                  }
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Video Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
                <h3 className="text-lg font-medium text-slate-900">Select Videos to Import</h3>
              </div>
              
              <div className="border border-slate-200 rounded-lg">
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={selectedVideos.size === combinedVideos.length && combinedVideos.length > 0}
                        onCheckedChange={selectAllVideos}
                      />
                      <Label className="text-sm font-medium text-slate-700">
                        Select All ({combinedVideos.length} videos)
                      </Label>
                    </div>
                    <div className="text-sm text-slate-600">
                      {selectedVideos.size} selected
                    </div>
                  </div>
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-slate-600">Loading videos...</p>
                    </div>
                  ) : combinedVideos.length === 0 ? (
                    <div className="p-8 text-center">
                      <p className="text-slate-600">No videos found. Connect platforms and try again.</p>
                    </div>
                  ) : (
                    combinedVideos.map((video) => {
                      return (
                        <div key={video.id} className="p-4 border-b border-slate-100 hover:bg-slate-50">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              checked={selectedVideos.has(video.id)}
                              onCheckedChange={() => toggleVideoSelection(video.id)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={video.thumbnailUrl || '/placeholder-video.png'}
                                  alt={video.title}
                                  className="w-12 h-8 rounded object-cover bg-slate-100"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <p className="font-medium text-slate-900 truncate">{video.title}</p>
                                    <Badge variant={video.platform === 'vimeo' ? 'default' : 'destructive'} className="text-xs">
                                      {video.platform === 'vimeo' ? 'Vimeo' : 'YouTube'}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center space-x-4 mt-1">
                                    <span className="text-sm text-slate-500">{formatDuration(video.duration)}</span>
                                    {video.platform === 'vimeo' && (video.originalData as VimeoVideo).tags && (video.originalData as VimeoVideo).tags!.length > 0 && (
                                      <span className="text-sm text-blue-600">
                                        {(video.originalData as VimeoVideo).tags!.length} tag{(video.originalData as VimeoVideo).tags!.length > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>

                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                
                <div className="p-4 bg-slate-50 flex justify-end">
                  <Button 
                    onClick={proceedToAssignment}
                    disabled={selectedVideos.size === 0}
                  >
                    Import Selected Videos
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Category Assignment */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">3</div>
                <h3 className="text-lg font-medium text-slate-900">Assign Categories & Publish Dates</h3>
              </div>
              
              <div className="space-y-4">
                {Array.from(selectedVideos).map((videoId) => {
                  const video = combinedVideos.find(v => v.id === videoId);
                  if (!video) return null;

                  const assignment = videoAssignments[videoId] || {
                    categoryId: '',
                    publishDate: new Date().toISOString().split('T')[0],
                    contentType: 'information'
                  };

                  return (
                    <div key={videoId} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3 mb-4">
                        <img
                          src={video.thumbnailUrl || '/placeholder-video.png'}
                          alt={video.title}
                          className="w-16 h-12 rounded object-cover bg-slate-100"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-900">{video.title}</h4>
                          <p className="text-sm text-slate-600 mt-1">
                            {video.platform === 'vimeo' ? 'Vimeo' : 'YouTube'} • {formatDuration(video.duration)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Category Selection */}
                        <div>
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">Category</Label>
                          <Select
                            value={assignment.categoryId}
                            onValueChange={(value) => {
                              setVideoAssignments(prev => ({
                                ...prev,
                                [videoId]: { ...assignment, categoryId: value }
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((category) => (
                                <SelectItem key={category.id} value={category.id || ''}>
                                  <div className="flex items-center space-x-2">
                                    <div className={`w-3 h-3 rounded-full ${
                                      category.module?.toLowerCase() === 'body' ? 'bg-[#d059c4]' :
                                      category.module?.toLowerCase() === 'mind' ? 'bg-[#66cee7]' :
                                      category.module?.toLowerCase() === 'general' ? 'bg-[#595ebe]' :
                                      'bg-slate-400'
                                    }`}></div>
                                    <span>{category.title}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Content Type */}
                        <div>
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">Content Type</Label>
                          <Select
                            value={assignment.contentType}
                            onValueChange={(value) => {
                              setVideoAssignments(prev => ({
                                ...prev,
                                [videoId]: { ...assignment, contentType: value }
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="information">Information</SelectItem>
                              <SelectItem value="exercise">Exercise</SelectItem>
                              <SelectItem value="meditation">Meditation</SelectItem>
                              <SelectItem value="tutorial">Tutorial</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Publish Date */}
                        <div>
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">Publish Date</Label>
                          <Input
                            type="date"
                            value={assignment.publishDate}
                            onChange={(e) => {
                              setVideoAssignments(prev => ({
                                ...prev,
                                [videoId]: { ...assignment, publishDate: e.target.value }
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                  >
                    Back to Selection
                  </Button>
                  <Button
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await saveToFirestore();
                        toast({
                          title: "Videos imported successfully",
                          description: `${selectedVideos.size} videos have been imported to your library.`,
                        });
                        onVideosSaved();
                        handleClose();
                      } catch (error: any) {
                        toast({
                          title: "Failed to import videos",
                          description: error.message,
                          variant: "destructive",
                        });
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading || Array.from(selectedVideos).some(videoId => 
                      !videoAssignments[videoId]?.categoryId
                    )}
                  >
                    {loading ? 'Importing...' : 'Import Videos'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
